import { Button, Space, Text } from '@gr-monorepo/common/ui';
import { MunicipalityRatings, Olympiad } from 'features/olympiads/model';
import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import Chart, { ChartRow } from './Chart';
import useUserData from 'hooks/useUserData';
import { LogClick, useClickStreamContext } from 'features/shared/ClickStream';
import RatingImproveResultPopup from 'features/shared/RatingImproveResultPopup';
import Tooltip, { TooltipPlacement } from 'features/shared/Tooltip';
import { Warning } from './OlympiadDetailsStyled';

const GrayPlate = styled.div`
  border-radius: 20px;
  background: #f4f5f6;
  padding: 24px;
`;

const Flex = styled.div`
  display: flex;
`;

const Title = styled.div`
  font-size: 18px;
  font-weight: 700;
  line-height: 24px;
`;
const SubTitle = styled.div`
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
  color: rgba(9, 21, 38, 0.6);
`;

const EmptyRatingsContent = (props: { link: string; olympiadId: string; popupText: string; showPopup: () => void }) => {
  return (
    <Flex>
      <span style={{ fontSize: 14, lineHeight: '20px' }}>
        Учащиеся вашего муниципалитета пока не приняли участие в олимпиаде. Для участия в&nbsp;рейтинге направьте
        информацию о соревновании в образовательные учреждения.
      </span>
      <Space size={24} />
      {props.popupText ? (
        <>
          <LogClick
            eventType="olymp_click_information_link"
            eventInfo="Олимпиада: Клик по кнопке проинформировать"
            cd1={props.olympiadId}
          >
            <Button color={'violet'} onClick={props.showPopup}>
              проинформировать
            </Button>
          </LogClick>
        </>
      ) : (
        <>
          <LogClick
            eventType="olymp_click_information_link"
            eventInfo="Олимпиада: Клик по кнопке проинформировать"
            cd1={props.olympiadId}
          >
            <Button color={'violet'} href={props.link} target="blank" as="a">
              проинформировать
            </Button>
          </LogClick>
        </>
      )}
    </Flex>
  );
};

const Ratings = (props: {
  olympiadId: string;
  data?: MunicipalityRatings['ratings'];
  informationLink: string;
  isPassedOlympiad: Boolean;
  popupText: string | null;
  hasValueByRealStudentsCount: boolean;
}) => {
  const userData = useUserData();
  const { logPageEvent } = useClickStreamContext();

  const [showImproveResultPopup, setShowImproveResultPopup] = useState(false);

  const isEmpty = useMemo(() => {
    return (!props.data || !props.data.length || !props.data.some((el) => el.value !== 0)) && !props.isPassedOlympiad;
  }, [props.data]);

  const rows = useMemo(
    () =>
      props.data
        ?.filter((el) => el.value > 0 || userData?.id === el.subject_id)
        .sort((a, b) =>
          b.value === a.value
            ? a.subject_id === userData?.id
              ? -1
              : b.subject_id === userData?.id
              ? 1
              : b.subject_name > a.subject_name
              ? -1
              : 1
            : b.value - a.value,
        )
        .map((el) => ({ title: el.subject_name, value: el.value, isCurrent: userData?.id === el.subject_id })) || [],
    [props.data],
  );
  const leaderRows = rows.slice(0, 3).filter((el) => !!el.value);

  const color = props.isPassedOlympiad ? 'rgba(194, 240, 139, 1)' : '#DBB2FF';
  const selfRow = rows.find((el) => el.isCurrent);
  const selfRowIndex = selfRow ? rows.indexOf(selfRow) : null;
  const isLeader = selfRow?.value! > 0 && selfRowIndex! < 3;

  const buttonLabel = useMemo(() => {
    if (isLeader && selfRowIndex === 0)
      return (
        <>
          Сохраните лидирующую позицию
          <br /> в рейтинге
        </>
      );
    if (isLeader && selfRowIndex! < 2)
      return (
        <>
          Ваш муниципалитет может стать
          <br /> абсолютным лидером рейтинга
        </>
      );

    return (
      <>
        Окажите методическую поддержку
        <br /> учителям своего муниципалитета
      </>
    );
  }, [selfRowIndex]);

  if (!props.isPassedOlympiad && isEmpty)
    return (
      <GrayPlate>
        <EmptyRatingsContent
          link={props.informationLink}
          olympiadId={props.olympiadId}
          popupText={props.popupText || ''}
          showPopup={() => setShowImproveResultPopup(true)}
        />
        {showImproveResultPopup && (
          <RatingImproveResultPopup
            title={'ПРОИНФОРМИРОВАТЬ'}
            text={props.popupText!}
            eventPrefix="olymp_rating"
            eventTitlePrefix="Олимпиада рейтинг"
            onClose={() => {
              setShowImproveResultPopup(false);
            }}
            headerColor={'#985EFF'}
          />
        )}
      </GrayPlate>
    );
  return (
    <>
      {!props.isPassedOlympiad && (
        <>
          <Text Grey>
            Оцените активность учеников вашего муниципалитета в текущей олимпиаде по сравнению
            <br /> с учениками других муниципалитетов вашего региона.
          </Text>
          <Space size={12} axis="vertical" />
        </>
      )}
      <GrayPlate>
        <Title>Региональный рейтинг активности муниципалитетов </Title>
        <Space size={24} axis="vertical" />
        <Chart
          rows={leaderRows}
          color={color}
          header={
            props.hasValueByRealStudentsCount
              ? 'Доля участников от общего количества учеников муниципалитета'
              : 'Доля участников олимпиады (от числа учеников, занимающихся на платформе)'
          }
        />
        <Space axis="vertical" size={32} />
        {!isLeader && selfRow ? (
          <>
            <SubTitle>{props.isPassedOlympiad ? 'Ваш результат' : 'Ваш текущий результат'}</SubTitle>
            <Space axis="vertical" size={8} />
            <ChartRow {...selfRow} color={color}></ChartRow>
            <Space axis="vertical" size={16} />
          </>
        ) : null}
        {!props.isPassedOlympiad && (
          <Flex>
            {props.popupText ? (
              <>
                <LogClick
                  eventType="olymp_click_improve_result_link"
                  eventInfo="Олимпиада: Клик по кнопке улучшить результат"
                  cd1={props.olympiadId}
                >
                  <Button color={'violet'} onClick={() => setShowImproveResultPopup(true)}>
                    улучшить результат
                  </Button>
                </LogClick>
              </>
            ) : (
              <>
                <LogClick
                  eventType="olymp_click_improve_result_link"
                  eventInfo="Олимпиада: Клик по кнопке улучшить результат"
                  cd1={props.olympiadId}
                >
                  <Button color={'violet'} href={props.informationLink} target="blank" as="a">
                    улучшить результат
                  </Button>
                </LogClick>
              </>
            )}
            <Space size={24} />
            <span style={{ fontSize: 14, lineHeight: '20px', color: 'rgba(9, 21, 38, 0.60)' }}>{buttonLabel}</span>
            {showImproveResultPopup && (
              <RatingImproveResultPopup
                text={props.popupText!}
                eventPrefix="olymp_rating"
                eventTitlePrefix="Олимпиада рейтинг"
                onClose={() => {
                  setShowImproveResultPopup(false);
                }}
                headerColor={'#985EFF'}
              />
            )}
          </Flex>
        )}
      </GrayPlate>
    </>
  );
};

export default Ratings;
